import { useState } from 'react';
import { Loader2, RefreshCw, ShieldOff, ShieldX } from 'lucide-react';
import { toast } from 'sonner';

import AdminShell from '../components/AdminShell';
import { useAdminIpBlocks } from '../hooks/useAdminIpBlocks';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day:    '2-digit',
    month:  'short',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

function truncateHash(hash: string): string {
  return hash.slice(0, 12) + '…' + hash.slice(-6);
}

export default function IpBlocksPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, loading, lifting, refresh, lift } = useAdminIpBlocks({ page, pageSize });

  const handleLift = async (ipHash: string) => {
    try {
      await lift(ipHash);
      toast.success('IP block lifted — this address can register again.');
    } catch {
      toast.error('Failed to lift IP block');
    }
  };

  const pagination = data?.pagination;

  return (
    <AdminShell
      title="Blocked IPs"
      description="IP addresses that have hit the 3-account-per-30-days registration cap. Lifting a block deletes all registration records for that IP hash."
      actions={(
        <button
          onClick={() => refresh().catch(() => undefined)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      )}
    >
      <section className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        {loading && !data ? (
          <div className="flex items-center gap-2 text-zinc-500 p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-zinc-500">
            <ShieldX className="w-8 h-8 opacity-40" />
            <p className="text-sm">No IPs are currently blocked.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
                <th className="px-5 py-3 text-left font-medium">IP Hash</th>
                <th className="px-5 py-3 text-left font-medium">Registrations</th>
                <th className="px-5 py-3 text-left font-medium">First Seen</th>
                <th className="px-5 py-3 text-left font-medium">Last Seen</th>
                <th className="px-5 py-3 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {data.items.map((row) => (
                <tr key={row.ipHash} className="hover:bg-zinc-800/40 transition-colors">
                  <td className="px-5 py-3.5">
                    <span
                      className="font-mono text-xs text-zinc-100 cursor-default"
                      title={row.ipHash}
                    >
                      {truncateHash(row.ipHash)}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-red-500/10 text-red-400 border border-red-500/20">
                      {row.count} accounts
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs whitespace-nowrap">
                    {formatDate(row.firstSeen)}
                  </td>
                  <td className="px-5 py-3.5 text-zinc-400 text-xs whitespace-nowrap">
                    {formatDate(row.lastSeen)}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => handleLift(row.ipHash)}
                      disabled={lifting === row.ipHash}
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-zinc-700 text-zinc-300 hover:border-red-500/50 hover:text-red-400 transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {lifting === row.ipHash ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <ShieldOff className="w-3 h-3" />
                      )}
                      Lift
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>{pagination.total} blocked IP{pagination.total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="h-7 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <span className="text-zinc-400">{page} / {pagination.totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="h-7 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
